/**
 * Seletor de documentos para candidato — um botão, feedback claro (PWA / Android).
 */
import React, { useCallback, useRef, useState } from 'react';
import { apiClient } from '../../api/apiClient';
import {
  CANDIDATE_DOC_ACCEPT,
  CANDIDATE_DOC_FORMATS_LABEL,
  candidateDocumentRejectReason,
  isAllowedCandidateDocumentFile,
} from '../../utils/candidateDocumentUpload';
import { friendlyNetworkError } from '../../utils/friendlyErrors';

async function uploadDocumentWithRetry(file, docType, retries = 1) {
  let lastErr;
  for (let attempt = 0; attempt <= retries; attempt += 1) {
    try {
      return await apiClient.uploadDocument(file, docType);
    } catch (err) {
      lastErr = err;
      const msg = (err?.message || '').toString();
      const isNetwork = /failed to fetch|networkerror|load failed|network request failed/i.test(msg);
      if (!isNetwork || attempt >= retries) break;
    }
  }
  throw lastErr;
}

export function CandidateDocumentFilePicker({
  docType,
  documents = [],
  onDocumentsChange,
  disabled = false,
  onUploadingChange,
  label = 'Documentos *',
}) {
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [localError, setLocalError] = useState('');

  const processFiles = useCallback(async (fileList) => {
    if (!fileList?.length || disabled || uploading) return;
    setUploading(true);
    onUploadingChange?.(true);
    setLocalError('');
    const added = [];
    const errors = [];

    for (const file of Array.from(fileList)) {
      if (!isAllowedCandidateDocumentFile(file)) {
        errors.push(candidateDocumentRejectReason(file) || `Arquivo não aceito: ${file.name || 'sem nome'}`);
        continue;
      }
      try {
        const { url, name } = await uploadDocumentWithRetry(file, docType);
        added.push({ url, name: name || file.name, size: file.size, type: file.type });
      } catch (err) {
        const raw = (err?.message || '').toString();
        let msg = friendlyNetworkError(raw);
        if (/failed to fetch|network/i.test(raw) && file.name?.toLowerCase().endsWith('.pdf')) {
          msg += ' PDFs grandes podem falhar no celular: use Wi‑Fi, reduza o tamanho (máx. 5MB) ou envie uma foto nítida do documento.';
        }
        errors.push(`${file.name || 'Documento'}: ${msg}`);
      }
    }

    if (added.length > 0) {
      onDocumentsChange([...documents, ...added]);
    }
    if (errors.length > 0) {
      setLocalError(errors.join(' '));
    }
    setUploading(false);
    onUploadingChange?.(false);
  }, [disabled, uploading, documents, docType, onDocumentsChange, onUploadingChange]);

  const onFileChange = (e) => {
    processFiles(e.target.files);
    e.target.value = '';
  };

  const removeAt = (index) => {
    onDocumentsChange(documents.filter((_, i) => i !== index));
  };

  return (
    <div style={{ marginBottom: 20 }}>
      <label style={{ display: 'block', marginBottom: 8, fontWeight: 500, color: '#374151' }}>
        {label}
      </label>

      {localError && (
        <div
          role="alert"
          style={{
            marginBottom: 12,
            padding: '10px 12px',
            borderRadius: 8,
            backgroundColor: '#FEF2F2',
            border: '1px solid #FECACA',
            color: '#991B1B',
            fontSize: 13,
            lineHeight: 1.45,
          }}
        >
          {localError}
        </div>
      )}

      {uploading && (
        <p style={{ margin: '0 0 12px 0', fontSize: 13, color: '#1D4ED8', fontWeight: 500 }}>
          Enviando arquivo… aguarde (PDFs podem demorar um pouco).
        </p>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept={CANDIDATE_DOC_ACCEPT}
        multiple
        onChange={onFileChange}
        style={{ display: 'none' }}
      />
      <button
        type="button"
        disabled={disabled || uploading}
        onClick={() => fileInputRef.current?.click()}
        style={{
          width: '100%',
          padding: '18px 16px',
          borderRadius: 8,
          border: '2px dashed #D97706',
          backgroundColor: '#FFFBEB',
          color: '#92400E',
          fontSize: 15,
          fontWeight: 600,
          cursor: disabled || uploading ? 'not-allowed' : 'pointer',
          opacity: disabled || uploading ? 0.65 : 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 6,
        }}
      >
        <span style={{ fontSize: 24 }}>📄</span>
        <span>Escolher documento</span>
      </button>

      {documents.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <p style={{ fontSize: 14, fontWeight: 500, marginBottom: 8, color: '#374151' }}>
            Documento anexado:
          </p>
          {documents.map((doc, index) => (
            <div
              key={`${doc.url || doc.name}-${index}`}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '8px 12px',
                backgroundColor: '#F0FDF4',
                border: '1px solid #BBF7D0',
                borderRadius: 6,
                marginBottom: 4,
              }}
            >
              <span style={{ fontSize: 14, color: '#166534' }}>
                ✓ {doc.name}{doc.size ? ` (${(doc.size / 1024 / 1024).toFixed(2)} MB)` : ''}
              </span>
              <button
                type="button"
                disabled={disabled || uploading}
                onClick={() => removeAt(index)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#DC2626',
                  cursor: 'pointer',
                  fontSize: 16,
                  padding: 4,
                }}
                aria-label="Remover documento"
              >
                ✕
              </button>
            </div>
          ))}
        </div>
      )}

      <p style={{ fontSize: 12, color: '#6B7280', marginTop: 8, marginBottom: 0, lineHeight: 1.45 }}>
        Envie <strong>um</strong> arquivo por vez — {CANDIDATE_DOC_FORMATS_LABEL} (máx. 5MB).
        Pode ser PDF <em>ou</em> foto; não é necessário enviar os dois.
        Confira se o nome aparece acima antes de tocar em Enviar.
      </p>
    </div>
  );
}
